import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useUserFlow } from '../contexts/UserFlowContext';
import Button from '../components/ui/Button';
import { Rocket, Zap } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { startDemo } = useUserFlow();

  const handleTryDemo = () => {
    startDemo();
    navigate('/debts');
  };

  // Debt Snowball Steps
  const snowballSteps = [
    {
      title: 'List your debts, smallest to largest',
      desc: 'Don’t worry about interest rates — pay smallest balance first'
    },
    {
      title: 'Pay minimums on everything',
      desc: 'Keep all accounts current and in good standing'
    },
    {
      title: 'Focus all extra money on the smallest debt',
      desc: 'Every spare pound goes to the smallest balance'
    },
    {
      title: 'Roll payments forward',
      desc: "When one's paid off, roll that payment to the next smallest debt"
    },
    {
      title: 'Repeat until debt-free',
      desc: 'The snowball gets bigger with each debt you eliminate'
    }
  ];


  return (
    <div className={`min-h-screen ${colors.background} ${colors.text.primary} px-6 py-12`}>
      <header className="text-center mb-6 max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-primary mb-4">
          Be Debt-Free Years Sooner
        </h1>
        <p className="text-lg leading-relaxed mb-4 font-semibold">
          Track progress, get AI coaching, and celebrate every win — without shame or stress.
        </p>
        <p className="text-base mb-6 text-gray-600">
          Join the growing community building a clear path to debt freedom.
        </p>
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          <Button
            size="lg"
            leftIcon={Rocket}
            onClick={handleTryDemo}
          >
            Start Your Free Plan
          </Button>
          <Button
            size="lg"
            variant="muted"
            leftIcon={Zap}
            onClick={() => navigate('/library/money-makeover')}
          >
            1-Month Makeover Challenge
          </Button>
        </div>
      </header>


      {/* Value Props */}
      <section className="mb-16 max-w-4xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          <button
            onClick={() => navigate('/plan')}
            className={`${colors.surface} rounded-lg p-6 text-center ${colors.border} border hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer group`}
          >
            <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">🎯</div>
            <h3 className="font-bold mb-2 text-primary group-hover:text-accent">Choose Your Strategy</h3>
            <p className={`text-sm ${colors.text.secondary}`}>
              Snowball, avalanche, or custom. It’s your plan.
            </p>
          </button>
          <button
            onClick={() => navigate('/plan')}
            className={`${colors.surface} rounded-lg p-6 text-center ${colors.border} border hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer group`}
          >
            <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">📊</div>
            <h3 className="font-bold mb-2 text-primary group-hover:text-accent">Track Real Progress
</h3>
            <p className={`text-sm ${colors.text.secondary}`}>
              Visualise your Debt Free Date
            </p>
          </button>
          <button
            onClick={() => navigate('/what-if')}
            className={`${colors.surface} rounded-lg p-6 text-center ${colors.border} border hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer group`}
          >
            <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">🔮</div>
            <h3 className="font-bold mb-2 text-primary group-hover:text-accent">Try What-If Scenarios
</h3>
            <p className={`text-sm ${colors.text.secondary}`}>
              Explore how extra payments speed things up.
            </p>
          </button>
        </div>
      </section>

      {/* Debt Snowball Method */}
      <section className="mb-16 max-w-4xl mx-auto">
        <div className={`${colors.surface} rounded-lg p-8 border-l-4 border-primary`}>
          <div className={`${colors.surfaceSecondary} rounded-lg p-6 mb-6 ${colors.border} border`}>
            <h3 className={`text-xl font-bold mb-4 ${colors.text.primary}`}>
              Snowball Method: Pay smallest debts first to build quick wins
            </h3>
            <div className="space-y-4">
              {snowballSteps.map((step, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{step.title}</p>
                    <p className="text-sm text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 space-y-2">
  <h3 className={`text-lg font-semibold ${colors.text.primary}`}>Why Snowball Works</h3>
  <p className={`${colors.text.secondary} text-base`}>
    Quick wins build momentum. When you feel progress, you stay motivated — and that’s what keeps you going.
    Psychology beats pure maths when it comes to changing behaviour.
  </p>
  <p className={`text-base font-semibold ${colors.text.primary}`}>
    Start small. Build momentum. Get free.
  </p>
</div>
        </div>
      </section>


      {/* 1-Month Makeover, What If Machine, AI Coach — unchanged for clarity */}
      {/* ... keep those sections as they are ... */}

      {/* Purpose Statement & Footer remain the same */}
      <section className={`${colors.surface} rounded-lg shadow-sm p-8 my-16 max-w-4xl mx-auto ${colors.border} border`}>
        <div className="text-center">
          <p className={`text-lg mb-4 ${colors.text.secondary}`}>TrySnowball exists for one reason:</p>
          <p className={`text-2xl font-bold mb-4 ${colors.text.primary}`}>
            To help you get out of debt, faster, without shame or gimmicks.
          </p>
          <p className={`text-lg ${colors.text.secondary}`}>Because you deserve better than minimum payments.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
